/**
 * Environment configuration utilities
 *
 * Provides helpers for accessing environment-specific configuration,
 * particularly for multi-tenant setups like ClawInstall.
 *
 * @since 1.0.0
 */

/**
 * Clerk key configuration
 */
export interface ClerkKeys {
  publishableKey: string;
}

/**
 * Brand flags used to select the correct Clerk application.
 *
 * @since 1.0.0
 */
export interface BrandClerkContext {
  readonly isClawInstall: boolean;
  /**
   * The resolved brand identity for the host. Consinna renders the ClawInstall
   * PRODUCT (so `isClawInstall` is `true` on its hosts) but authenticates
   * against its OWN Clerk instance / user pool, so key selection is keyed on the
   * identity, not the route prefix. Optional so callers without an identity
   * (e.g. the SSR fallback) fall through to the non-Consinna paths.
   */
  readonly identity?: { readonly id: string } | undefined;
  /**
   * Whether the host routes to the orca portal (`/orca`). The orca portal
   * authenticates against its OWN Clerk instance (`*_ORCALOOPS`), so key
   * selection branches on it. Optional so callers without the flag (e.g. the
   * SSR fallback) fall through to the default path.
   */
  readonly isOrca?: boolean | undefined;
}

/**
 * Get Clerk keys for the brand the request is on.
 *
 * ClawInstall runs on its **own** Clerk instance in production
 * (`clerk.clawinstall.ai`). The `*_CLAWINSTALL` vars hold those keys; when they
 * are unset (e.g. local dev, or before the production cutover) ClawInstall
 * transparently falls back to the default keys, so it shares the development
 * instance until the production keys are provided. Everything else
 * (FlirtMachine + unmapped hosts) uses the default application. Consinna hosts
 * use their OWN instance via `VITE_CLERK_PUBLISHABLE_KEY_CONSINNA` (a separate
 * user pool); see {@link BrandClerkContext.identity}. The orca portal likewise
 * uses `VITE_CLERK_PUBLISHABLE_KEY_ORCALOOPS`, falling back to the default key
 * while that var is absent; see {@link BrandClerkContext.isOrca}.
 *
 * @param brand - Resolved brand flags for the current request
 * @returns Clerk publishable key
 *
 * @since 1.0.0
 */
export const getClerkKeys = (brand: BrandClerkContext): ClerkKeys => {
  // Consinna is its own Clerk application (separate user pool from ClawInstall);
  // its hosts render the ClawInstall product but sign in against the Consinna
  // instance. Checked FIRST because a Consinna host is also `isClawInstall`.
  // No fallback to the ClawInstall key — that key is domain-locked to
  // clawinstall.ai and would be rejected on a consinna.ai origin.
  if (brand.identity?.id === "consinna") {
    return {
      publishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY_CONSINNA ?? "",
    };
  }
  if (brand.isClawInstall) {
    return {
      publishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY_CLAWINSTALL
        ?? import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
        ?? "",
    };
  }
  // The orca portal (orcaloops.ai) is its own Clerk application; while the
  // production var is absent it shares the default instance (a development
  // instance that is not domain-locked), so pre-cutover prod and `orca.test`
  // local dev keep working.
  if (brand.isOrca) {
    return {
      publishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY_ORCALOOPS
        ?? import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
        ?? "",
    };
  }
  return {
    publishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ?? "",
  };
};
